(() => {
  const SECTION_SELECTOR = '[data-delivery-returns]';

  const instances = new WeakMap();

  class DeliveryReturnsPage {
    constructor(section) {
      this.section = section;

      this.tabs = Array.from(
        section.querySelectorAll('[data-dr-tab]')
      );

      this.panels = Array.from(
        section.querySelectorAll('[data-dr-panel]')
      );

      this.accordions = Array.from(
        section.querySelectorAll('[data-dr-accordion]')
      );

      this.statePickers = Array.from(
        section.querySelectorAll('[data-dr-state-picker]')
      );

      this.onTabClick = this.onTabClick.bind(this);
      this.onAccordionClick = this.onAccordionClick.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);

      this.bindTabs();
      this.bindAccordions();
      this.initStatePickers();
      this.autoSelectCountryTab();

      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onKeydown);
    }

    bindTabs() {
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', this.onTabClick);
      });
    }

    bindAccordions() {
      this.accordions.forEach((accordion) => {
        const trigger = accordion.querySelector('[data-dr-accordion-trigger]');

        if (!trigger) return;

        trigger.addEventListener('click', this.onAccordionClick);
      });
    }

    initStatePickers() {
      this.statePickers.forEach((picker) => {
        const trigger = picker.querySelector('[data-dr-state-trigger]');
        const current = picker.querySelector('[data-dr-state-current]');
        const menu = picker.querySelector('[data-dr-state-menu]');
        const content = picker.querySelector('[data-dr-state-content]');
        const dropdown = picker.querySelector('[data-dr-state-dropdown]');
        const options = Array.from(
          picker.querySelectorAll('[data-dr-state-option]')
        );

        if (!trigger || !current || !menu || !content || !dropdown) return;

        let states = [];

        try {
          states = JSON.parse(picker.dataset.states || '[]');
        } catch (error) {
          states = [];
        }

        if (!Array.isArray(states) || !states.length) return;

        const defaultState = picker.dataset.defaultState || '';
        const firstState = states[0]?.state || states[0]?.name || '';
        const initialState = states.some((item) => {
          return (item.state || item.name) === defaultState;
        })
          ? defaultState
          : firstState;

        picker._deliveryReturnsStateData = {
          states,
          trigger,
          current,
          menu,
          content,
          dropdown,
          options,
          selectedState: initialState,
        };

        trigger.addEventListener('click', () => {
          this.toggleStateDropdown(picker);
        });

        options.forEach((option) => {
          option.addEventListener('click', () => {
            this.selectState(picker, option.dataset.state);
            this.closeStateDropdown(picker);
            trigger.focus();
          });
        });

        this.selectState(picker, initialState);
      });
    }

    autoSelectCountryTab() {
      const currentCountry =
        (
          window.Shopify?.country ||
          window.Shopify?.routes?.root?.split('/')[1] ||
          ''
        ).toUpperCase();

      if (!currentCountry) return;

      const matchedTab = this.tabs.find((tab) => {
        return (
          (tab.dataset.countryCode || '').toUpperCase() === currentCountry
        );
      });

      if (matchedTab) {
        matchedTab.click();
      }
    }

    onTabClick(event) {
      const tab = event.currentTarget;
      const targetId = tab.dataset.target;

      if (!targetId) return;

      this.tabs.forEach((item) => {
        const isActive = item === tab;

        item.classList.toggle('is-active', isActive);
        item.setAttribute('aria-selected', isActive ? 'true' : 'false');
        item.tabIndex = isActive ? 0 : -1;
      });

      this.panels.forEach((panel) => {
        const isActive = panel.id === targetId;

        panel.classList.toggle('is-active', isActive);
        panel.hidden = !isActive;
      });

      this.closeAllStateDropdowns();
    }

    onAccordionClick(event) {
      const trigger = event.currentTarget;
      const accordion = trigger.closest('[data-dr-accordion]');
      const content = accordion?.querySelector('[data-dr-accordion-content]');

      if (!accordion || !content) return;

      const isOpen = accordion.classList.contains('is-open');

      accordion.classList.toggle('is-open', !isOpen);
      trigger.setAttribute('aria-expanded', !isOpen ? 'true' : 'false');
      content.hidden = isOpen;
    }

    toggleStateDropdown(picker) {
      const data = picker._deliveryReturnsStateData;

      if (!data) return;

      const isOpen = data.dropdown.classList.contains('is-open');

      this.closeAllStateDropdowns();

      if (!isOpen) {
        data.dropdown.classList.add('is-open');
        this.section.classList.add('has-state-modal');
        this.lockMobilePageScroll();
        data.menu.hidden = false;
        data.trigger.setAttribute('aria-expanded', 'true');

        const activeOption = data.options.find((option) => {
          return option.classList.contains('is-active');
        });

        activeOption?.focus();
      }
    }

    closeStateDropdown(picker) {
      const data = picker._deliveryReturnsStateData;

      if (!data) return;

      data.dropdown.classList.remove('is-open');
      this.section.classList.remove('has-state-modal');
      this.unlockMobilePageScroll();
      data.menu.hidden = true;
      data.trigger.setAttribute('aria-expanded', 'false');
    }

    closeAllStateDropdowns() {
      this.statePickers.forEach((picker) => {
        this.closeStateDropdown(picker);
      });
    }

    lockMobilePageScroll() {
      if (!window.matchMedia('(max-width: 749px)').matches) return;

      document.documentElement.classList.add('dr-state-modal-open');
      document.body.classList.add('dr-state-modal-open');
    }

    unlockMobilePageScroll() {
      const hasOpenDropdown = this.statePickers.some((picker) => {
        return picker._deliveryReturnsStateData?.dropdown.classList.contains('is-open');
      });

      if (hasOpenDropdown) return;

      document.documentElement.classList.remove('dr-state-modal-open');
      document.body.classList.remove('dr-state-modal-open');
    }

    selectState(picker, stateName) {
      const data = picker._deliveryReturnsStateData;

      if (!data || !stateName) return;

      const selected = data.states.find((item) => {
        return (item.state || item.name) === stateName;
      });

      if (!selected) return;

      data.selectedState = stateName;
      data.current.textContent = stateName;

      data.options.forEach((option) => {
        const isActive = option.dataset.state === stateName;

        option.classList.toggle('is-active', isActive);
        option.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      data.content.innerHTML = selected.content || '';
    }

    onDocumentClick(event) {
      if (this.section.contains(event.target)) {
        const clickedInsideDropdown = event.target.closest('[data-dr-state-dropdown]');

        if (clickedInsideDropdown) return;
      }

      this.closeAllStateDropdowns();
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        this.closeAllStateDropdowns();
        return;
      }

      const option = event.target.closest('[data-dr-state-option]');

      if (!option || !this.section.contains(option)) return;

      const picker = option.closest('[data-dr-state-picker]');
      const data = picker?._deliveryReturnsStateData;

      if (!data) return;

      const currentIndex = data.options.indexOf(option);

      if (event.key === 'ArrowDown') {
        event.preventDefault();

        const nextOption = data.options[currentIndex + 1] || data.options[0];
        nextOption.focus();
        return;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();

        const previousOption =
          data.options[currentIndex - 1] || data.options[data.options.length - 1];

        previousOption.focus();
        return;
      }

      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();

        this.selectState(picker, option.dataset.state);
        this.closeStateDropdown(picker);
        data.trigger.focus();
      }
    }

    destroy() {
      this.tabs.forEach((tab) => {
        tab.removeEventListener('click', this.onTabClick);
      });

      this.accordions.forEach((accordion) => {
        const trigger = accordion.querySelector('[data-dr-accordion-trigger]');

        trigger?.removeEventListener('click', this.onAccordionClick);
      });

      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onKeydown);
    }
  }

  function getSections(root = document) {
    const sections = [];

    if (root.matches?.(SECTION_SELECTOR)) {
      sections.push(root);
    }

    root.querySelectorAll?.(SECTION_SELECTOR).forEach((section) => {
      sections.push(section);
    });

    return sections;
  }

  function init(root = document) {
    getSections(root).forEach((section) => {
      if (instances.has(section)) return;

      instances.set(section, new DeliveryReturnsPage(section));
    });
  }

  function unload(root) {
    getSections(root).forEach((section) => {
      instances.get(section)?.destroy();
      instances.delete(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), {
      once: true,
    });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    unload(event.target);
  });
})();
